"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight, Compass, ShieldCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AspectRatio } from "@/components/ui/aspect-ratio";
interface ProjectItem {
  id: string;
  title: string;
  category: string;
  neighborhood: string;
  description: string;
  image: string;
  srcset: string;
  sizes: string;
  alt: string;
}
const projects: ProjectItem[] = [{
  id: "project-1",
  title: "Light-filled primary suite addition",
  category: "Home addition",
  neighborhood: "Historic Brookhaven",
  description: "Vaulted ceilings, custom white-oak millwork, and full architectural integration matching original exterior brickwork.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_addition_suite.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_addition_suite.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_addition_suite.png-wvc-sizes",
  alt: "Spacious master bedroom suite addition with vaulted ceiling and natural daylight"
}, {
  id: "project-2",
  title: "Terrace-level entertainment lounge",
  category: "Basement remodel",
  neighborhood: "Peachtree Dunwoody",
  description: "Honed quartzite wet bar, climate-controlled wine display, and daylight acoustic media suite built for hosting.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/peachtree_dunwoody_basement.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/peachtree_dunwoody_basement.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/peachtree_dunwoody_basement.png-wvc-sizes",
  alt: "Custom finished terrace level basement featuring wet bar and acoustic media area"
}, {
  id: "project-3",
  title: "Vaulted screened outdoor living porch",
  category: "Covered porch",
  neighborhood: "Brittany Community",
  description: "Hand-laid stacked stone fireplace, integrated infrared heating, and phantom screening overlooking natural wooded topography.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brittany_screened_porch.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brittany_screened_porch.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brittany_screened_porch.png-wvc-sizes",
  alt: "Vaulted timber screened porch with stone fireplace and outdoor seating"
}, {
  id: "project-4",
  title: "Multi-tiered composite deck & kitchen",
  category: "Custom deck",
  neighborhood: "Lynwood Park",
  description: "Low-maintenance composite decking with recessed stair illumination, custom steel rail profiles, and integrated prep island.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/lynwood_park_custom_deck.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/lynwood_park_custom_deck.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/lynwood_park_custom_deck.png-wvc-sizes",
  alt: "Multi level architectural outdoor deck with built-in kitchen and modern metal railings"
}];
export default function Gallery() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 15
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="project-gallery" data-nav="light" className="relative bg-background py-20 md:py-24 text-foreground overflow-hidden">
      <div className="container mx-auto px-5 sm:px-8 lg:px-12 max-w-7xl">
        {/* Header Block */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 15
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }} className="mx-auto max-w-3xl text-center mb-16 md:mb-20">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-primary/35 bg-primary/10 mb-4">
            <Compass className="size-3.5 text-primary" />
            <span className="text-xs font-semibold tracking-wider text-primary uppercase">
              Selected Brookhaven work
            </span>
          </div>

          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.2] text-foreground mb-4">
            Recent residential additions and outdoor retreats
          </h2>

          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto mb-6">
            Explore architectural expansions, terrace-level lounges, and covered outdoor spaces designed specifically for Brookhaven living and entertaining.
          </p>

          <div className="h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent w-48 mx-auto" />
        </motion.div>

        {/* Portfolio Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
          {projects.map((item, index) => <motion.article key={item.id} data-index={index} variants={itemVariants} whileHover={shouldReduceMotion ? undefined : {
          y: -4
        }} transition={{
          duration: 0.25,
          ease: [0.2, 0.8, 0.2, 1]
        }} className="group relative rounded-xl border border-border bg-card overflow-hidden shadow-xs hover:shadow-lg hover:border-primary/60 transition-[box-shadow,border-color] duration-300 flex flex-col">
              <div className="relative overflow-hidden bg-muted">
                <AspectRatio ratio={3 / 2}>
                  <img src={item.image} data-wvc-srcset={item.srcset} data-wvc-sizes={item.sizes} alt={item.alt} loading="lazy" decoding="async" className="w-full h-full object-cover transform duration-500 ease-out group-hover:scale-105" />
                </AspectRatio>
                
                <div className="absolute top-4 left-4 flex flex-wrap gap-2 pointer-events-none">
                  <Badge variant="secondary" className="shadow-xs text-xs font-medium">
                    {item.category}
                  </Badge>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-background/90 text-foreground backdrop-blur-xs border border-border/80">
                    {item.neighborhood}
                  </span>
                </div>
              </div>

              <div className="p-6 md:p-8 flex flex-col flex-grow justify-between">
                <div>
                  <div className="flex items-start justify-between gap-4 mb-2.5">
                    <h3 className="font-serif text-xl sm:text-2xl font-semibold tracking-tight text-foreground group-hover:text-primary transition-colors duration-200">
                      {item.title}
                    </h3>
                    <div className="size-8 rounded-full border border-border flex items-center justify-center shrink-0 group-hover:bg-primary group-hover:text-primary-foreground group-hover:border-primary transition-[color,background-color,border-color] duration-200 text-muted-foreground">
                      <ArrowUpRight className="size-4" />
                    </div>
                  </div>

                  <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                    {item.description}
                  </p>
                </div>

                <div className="mt-6 pt-4 border-t border-border/70 flex items-center justify-between text-xs text-muted-foreground">
                  <span className="flex items-center gap-1.5 font-medium">
                    <ShieldCheck className="size-3.5 text-primary" />
                    Georgia General Contractor
                  </span>
                  <span className="font-sans">Brookhaven, GA</span>
                </div>
              </div>
            </motion.article>)}
        </motion.div>

        {/* Section Footer CTA */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 15
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.5
      }} transition={{
        duration: 0.5,
        delay: 0.2,
        ease: [0.16, 1, 0.3, 1]
      }} className="mt-14 md:mt-18 text-center">
          <div className="inline-flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg px-8 py-3.5 font-semibold text-sm shadow-sm transition-[background-color,box-shadow]">
              <Link to="/OurProjectsPage">
                View all our projects
              </Link>
            </Button>
            
            <Button asChild variant="outline" size="lg" className="border-border text-foreground hover:bg-accent rounded-lg px-7 py-3.5 font-medium text-sm transition-colors">
              <Link to="/ContactRequestAnEstimatePage">
                Request a consultation
              </Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}